import './style.scss'
import { Button, Dropdown, Input, message, Modal, Popover, Tooltip } from 'antd'
import { FC, memo, useEffect, useMemo, useRef, useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as Api from '@/api/home'
import ArrowLeft from '@/assets/arrow-left.png'
import AvatarAi from '@/assets/avatar-ai.png'
import AvatarPhoto from '@/assets/avatar-photo.png'
import AvatarVideo from '@/assets/avatar-video.png'
import Empty from '@/assets/empty.png'
import IconAdd from '@/assets/icon-add.png'
import MarketCircle1 from '@/assets/market-circle1.png'
import MarketCircle2 from '@/assets/market-circle2.png'
import MarketCircle3 from '@/assets/market-circle3.png'
import { Ellipsis, Play } from '@/assets/svg'
import CheckLogin from '@/components/CheckLogin'
import VideoModal from '@/components/VideoModal'
import { UserStore } from '@/global-states'
import { eventTracking, Render, urlSource } from '@/libs/util'
import {
  CopyOutlined,
  DeleteOutlined,
  DeliveredProcedureOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FormOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  StarFilled
} from '@ant-design/icons'
import VideoBox from '../video/components/VideoBox'
import CreateAvatarDrawer from './components/CreatAvatarDrawer'
import CreateAiAvatarNewDrawer from './components/CreateAiAvatarNewDrawer'
import CreateAiMaterialDrawer from './components/CreateAiMaterialDrawer'
import CreatePhotoAvatarDrawer from './components/CreatePhotoAvatarDrawer'
import AvatarFormModalEdit from './components/FormModalEdit'
import Groups from './components/Groups'

const Index: FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation(['system', 'avatar'])
  const { loginStatus } = UserStore
  const [searchParams] = useSearchParams()
  const [preview, setPreview] = useState<any>()
  const [openId, setOpenId] = useState<any>()
  const [data, setData] = useState<any[]>()
  const [globalData, setGlobalData] = useState<any[]>([])
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false)
  const [createPhotoDrawerOpen, setCreatePhotoDrawerOpen] = useState(false)
  const [createAIDrawerOpen, setCreateAIModalOpen] = useState(false)
  const [modelModal, setModelModal] = useState<any>()
  const [group, setGroup] = useState<any>()
  const [avatarId, setAvatarId] = useState<number>()
  const [groupHumans, setGroupHumans] = useState<any[]>([])
  const [groupPhotos, setGroupPhotos] = useState<any[]>()
  const [groupCreations, setGroupCreations] = useState<any[]>()
  const [activeIndex, setActiveIndex] = useState(0)
  const [bookHumans, setBookHumans] = useState<any[]>([])
  const [editAvatar, setEditAvatar] = useState<any>()
  const [avatarSize, setAvatarSize] = useState(10)
  const [detailSize, setDetailSize] = useState(8)
  const [avatarPage, setAvatarPage] = useState(1)
  const [photoPage, setPhotoPage] = useState(1)
  const [videoPage, setVideoPage] = useState(1)
  const [formModalOpen, setFormModalOpen] = useState(false)
  const [createAiMaterialOpen, setCreateAiMaterialOpen] = useState(false)
  const [createAiMaterialMode, setCreateAiMaterialMode] = useState(1)
  const [photoInfo, setPhotoInfo] = useState<any>()
  const timeRef = useRef<any>()
  const [tipType, setTipType] = useState<any>()

  const renderData = useMemo(() => {
    if (activeIndex === 1) {
      return bookHumans
    }
    return data
  }, [activeIndex, data, bookHumans])

  const showAvatarData = useMemo(() => {
    return renderData?.slice(0, avatarPage * avatarSize - 1)
  }, [renderData, avatarPage])

  const showGroupPhotos = useMemo(() => {
    return groupPhotos?.slice(0, photoPage * detailSize - 1)
  }, [groupPhotos, photoPage])

  const showGroupHumans = useMemo(() => {
    if (group?.global) {
      return groupHumans
    }
    return groupHumans?.slice(0, videoPage * detailSize - 1)
  }, [groupHumans, videoPage, group])

  useEffect(() => {
    eventTracking('InstantAvatarPage')
    const mode = searchParams.get('mode')
    if (mode == '1') {
      toCreatePhotoAvatar()
    }
    if (mode == '2') {
      toCreateAvatar()
    }
    if (mode == '3') {
      toCreateAIAvatar()
    }
    return () => clearEvent()
  }, [])

  useEffect(() => {
    if (group?.id) {
      getDetail()
    }
  }, [group])

  useEffect(() => {
    setAvatarPage(1)
  }, [activeIndex])

  useEffect(() => {
    getData()
    getGlobalData()
    getBookedHumans()
    initPageSize()
  }, [loginStatus])

  const initPageSize = () => {
    const width = window.innerWidth
    if (width >= 3200) {
      setAvatarSize(18)
      setDetailSize(16)
    } else if (width >= 2700) {
      setAvatarSize(16)
      setDetailSize(14)
    } else if (width >= 2300) {
      setAvatarSize(14)
      setDetailSize(12)
    } else if (width >= 1800) {
      setAvatarSize(12)
      setDetailSize(10)
    } else if (width >= 1280) {
      setAvatarSize(10)
      setDetailSize(8)
    } else if (width >= 884) {
      setAvatarSize(8)
      setDetailSize(6)
    }
  }

  const getDetail = async () => {
    setPhotoPage(1)
    setVideoPage(1)
    await getGroupHumans()
    await getGroupPhotos()
    getGroupCreations()
    loopGroupHumans()
  }

  const getData = async (toDetail?: boolean) => {
    const res = await Api.getDigitalHumanGroups()
    setData(res?.list || [])
    if (toDetail) {
      setGroup(res?.list?.[0])
    }
  }

  const getGlobalData = async () => {
    const res = await Api.getGlobalDigitalHumanGroups()
    setGlobalData(res?.list || [])
  }

  const getGroupHumans = async () => {
    const res = await Api.getDigitalHumansByGroup(group.id as number)
    setGroupHumans(res.list || [])
    if (!(res?.list || []).filter((d: any) => d.status === 1)?.length) {
      clearEvent()
    }
  }

  const getGroupPhotos = async () => {
    const res = await Api.getDigitalPhotosByGroup(group.id as number)
    setGroupPhotos(res?.list || [])
  }

  const getGroupCreations = async () => {
    const res = await Api.getDigitalCreationsByGroup(group.id as number)
    setGroupCreations(res?.list || [])
  }

  const getBookedHumans = async () => {
    try {
      const res = await Api.getBookmarkedCommunityHumans()
      setBookHumans(
        (res?.list || []).map((l: any) => {
          return {
            cover_url: l.merchandise.id > 349 ? l.merchandise.covers[0] : l.merchandise.images[0],
            id: l.group_id,
            member_count: l.merchandise.videos.length || 0,
            title: l.merchandise.name,
            booked: true
          }
        })
      )
    } catch {
      setBookHumans([])
    }
  }

  const loopGroupHumans = () => {
    clearEvent()
    timeRef.current = setInterval(() => {
      getGroupHumans()
    }, 5000)
  }

  const clearEvent = () => {
    if (timeRef.current) {
      clearInterval(timeRef.current)
      timeRef.current = undefined
    }
  }

  const previeGroup = (g: any) => {
    setGroup(g)
  }

  const previewVideo = (d: any) => {
    setOpenId(undefined)
    setPreview({
      url: d.video_url_v2 || d.video_url,
      title: d.title,
      tip: d.tip,
      id: d.id,
      source_type: d.source_type
    })
  }

  const deleteVideo = async (id: any, e: any) => {
    e.stopPropagation()
    Modal.confirm({
      title: t('avatar.delete_video_title'),
      content: t('avatar.delete_video_content'),
      okButtonProps: {
        danger: true
      },
      icon: <InfoCircleOutlined style={{ color: '#e54945' }} />,
      onOk: async () => {
        setOpenId(undefined)
        await Api.deleteDigitalHumans(id)
        getGroupHumans()
        message.success(t('system.delete_success'))
      }
    })
  }

  const deletePhoto = async (id: any, e: any) => {
    e.stopPropagation()
    Modal.confirm({
      title: t('avatar.delete_photo_title'),
      content: t('avatar.delete_photo_content'),
      okButtonProps: {
        danger: true
      },
      icon: <InfoCircleOutlined style={{ color: '#e54945' }} />,
      onOk: async () => {
        setOpenId(undefined)
        await Api.deleteDigitalPhoto(group?.id, id)
        getGroupPhotos()
        message.success(t('system.delete_success'))
      }
    })
  }

  const renameAvatar = (d: any, e: any) => {
    e.stopPropagation()
    setOpenId(undefined)
    setEditAvatar(d)
  }

  const sureRename = async () => {
    if (group?.id) {
      if (editAvatar?.type === 'photo') {
        await Api.updateDigitalPhoto(group.id, editAvatar.id, {
          title: editAvatar.title
        })
        getGroupPhotos()
      } else {
        await Api.updateDigitalHumans(editAvatar.id, {
          title: editAvatar.title
        })
        getGroupHumans()
      }
    } else {
      await Api.updateDigitalGroup(editAvatar.id, {
        title: editAvatar.title
      })
      getData()
    }
    message.success(t('system.update_success'))
    setEditAvatar(undefined)
  }

  const deleteGroup = (id: any, e: any) => {
    e.stopPropagation()
    Modal.confirm({
      title: t('avatar.delete_group_title'),
      content: <div>{t('avatar.delete_group_content')}</div>,
      okButtonProps: {
        danger: true
      },
      icon: <InfoCircleOutlined style={{ color: '#e54945' }} />,
      onOk: async () => {
        setOpenId(undefined)
        await Api.deleteDigitalGroup(id)
        getData()
        message.success(t('system.delete_success'))
      }
    })
  }

  const moveGroup = (id: number, e: any) => {
    e.stopPropagation()
    setOpenId(undefined)
    setAvatarId(id)
  }

  const toChooseModel = () => {
    eventTracking('InstantAvatarCloneClick')
    setModelModal('avatar')
  }

  const toChooseVideoModel = () => {
    setGroup({
      ...group,
      member_count: groupHumans.length || 0
    })
    setModelModal('video')
  }

  const toChoosePhotoModel = () => {
    setGroup({
      ...group,
      member_count: groupPhotos?.length
    })
    setModelModal('photo')
  }

  const toCreateAvatar = () => {
    eventTracking('InstantAvatarVideoCloneClick')
    setModelModal(undefined)
    setCreateDrawerOpen(true)
  }

  const toCreatePhotoAvatar = () => {
    eventTracking('InstantAvatarPhotoCloneClick')
    setModelModal(undefined)
    setCreatePhotoDrawerOpen(true)
  }

  const toCreateAIAvatar = async () => {
    eventTracking('InstantAvatarAICloneClick')
    setModelModal(undefined)
    setCreateAIModalOpen(true)
  }

  const toCreateVideo = (d: any) => {
    eventTracking('CreateAvatarClick', {
      avatarId: d.id
    })
    if (d.tip) {
      return Modal.confirm({
        title: t('system.tip'),
        content: t('avatar.video_tip_warning', { tip: d.tip }),
        okText: t('system.continue_create'),
        onOk: () => {
          navigate(`/create-video/${d.id}`)
        }
      })
    }

    navigate(`/create-video/${d.id}`)
  }

  const groupToCreateVideo = async (d: any) => {
    const res = await Api.getDigitalHumansByGroup(d.id as number)
    if (res?.list?.length) {
      const l = res.list.find((l: any) => l.status === 2)
      if (!l) {
        message.warning(t('avatar.no_available_video'))
      } else {
        eventTracking('CreateAvatarGroupClick', {
          avatarId: l.id
        })
        navigate(`/create-video/${l.id}`)
      }
    } else {
      message.warning(t('avatar.no_available_video'))
    }
  }

  const onCreateSuccess = async (type?: any) => {
    if (group?.id) {
      getGroupHumans()
      loopGroupHumans()
      getGroupPhotos()
    } else {
      await getData(true)
      setTimeout(() => {
        setTipType(type)
      }, 200)
    }
  }

  const cancelCollect = async (groupId: number, e: any) => {
    e.stopPropagation()
    await Api.cancelBookmarkedCommunityHumans(groupId)
    message.success(t('system.collect_cancel_success'))
    getBookedHumans()
  }

  const loadAvatarMore = () => {
    setAvatarPage((page) => page + 1)
  }

  const loadVideoMore = () => {
    setVideoPage((page) => page + 1)
  }

  const loadPhotoMore = () => {
    setPhotoPage((page) => page + 1)
  }

  return (
    <div className="page-common page-home">
      <div className="common-header">
        {group?.id ? (
          <>
            <img
              src={ArrowLeft}
              onClick={() => {
                setGroup(undefined)
                setGroupHumans([])
                setGroupPhotos([])
                setGroupCreations([])
                getData()
              }}
              className="arrow-left"
            />
            <div className="cover">
              <img src={group.face_url || group.cover_url} />
            </div>
            <div className="text">{group.title}</div>
            {!group?.global && <FormOutlined onClick={() => setFormModalOpen(true)} />}
          </>
        ) : (
          <div className="text">{t('avatar.avatar')}</div>
        )}
      </div>
      <div className="common-wrapper" style={{ display: !group?.id ? 'block' : 'none' }}>
        <div className="home-tabs">
          <div className={`tabs-item ${activeIndex === 0 ? 'actived' : ''}`} onClick={() => setActiveIndex(0)}>
            <span>{t('avatar.my_avatars')}</span>
          </div>
          <div className={`tabs-item ${activeIndex === 1 ? 'actived' : ''}`} onClick={() => setActiveIndex(1)}>
            <span>{t('avatar.my_avatars_collect')}</span>
          </div>
        </div>

        <div className="page-container-cover">
          {activeIndex === 0 && data && (
            <div className="list-image-box">
              <div className="box-main">
                <CheckLogin>
                  <div className={`empty-group ${data.length > 0 ? 'data' : ''}`} onClick={toChooseModel}>
                    <div className="center">
                      <img className="icon-add" src={IconAdd} />
                      <div className="text gradient">{t('avatar.avatar_quickly_create')}</div>
                    </div>
                  </div>
                </CheckLogin>
              </div>
            </div>
          )}
          {showAvatarData?.map((d) => (
            <div className="list-image-box" key={`${d.id}-${d.title}`}>
              <div className="box-main">
                <div className="image">
                  <img
                    src={d.face_url || d.cover_url}
                    onClick={() => {
                      previeGroup({
                        ...d,
                        global: activeIndex === 1
                      })
                    }}
                  />

                  <Dropdown
                    menu={{
                      items:
                        activeIndex === 0
                          ? [
                              {
                                key: '1',
                                label: (
                                  <div className="dropdown-list" onClick={renameAvatar.bind(this, d)}>
                                    <EditOutlined />
                                    {t('system.rename')}
                                  </div>
                                )
                              },
                              {
                                key: '2',
                                label: (
                                  <div className="dropdown-list" onClick={deleteGroup.bind(this, d.id)}>
                                    <DeleteOutlined />
                                    {t('system.delete')}
                                  </div>
                                )
                              }
                            ]
                          : [
                              {
                                key: '1',
                                label: (
                                  <div className="dropdown-list" onClick={cancelCollect.bind(this, d.id)}>
                                    <StarFilled />
                                    {t('system.collect_cancel')}
                                  </div>
                                )
                              }
                            ]
                    }}
                    placement="bottomRight"
                    open={openId === d.id}
                    onOpenChange={(open: boolean) => setOpenId(open ? d.id : undefined)}
                  >
                    <div className="op">
                      <Button type="primary" className="btn" onClick={(e) => e.stopPropagation()}>
                        <Ellipsis />
                      </Button>
                    </div>
                  </Dropdown>
                </div>
                <div className="bottom">
                  <div className="title ellipsis">{d.title}</div>
                  <Button type="primary" className="btn" onClick={groupToCreateVideo.bind(this, d)}>
                    {t('avatar.to_create')}
                  </Button>
                  <span className="num">
                    <label className="gradient">
                      {t('avatar.material_count', { count: (d.member_count || 0) + (d.image_count || 0) })}
                    </label>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {!!renderData?.length && showAvatarData?.length !== renderData?.length && (
          <div className="view-more">
            <span className="gradient" onClick={loadAvatarMore}>
              {t('system.show_more')}
            </span>
          </div>
        )}

        {activeIndex === 1 && !renderData?.length && (
          <div className="data-empty">
            <img className="empty" src={Empty} />
            <p>
              {t('avatar.no_collections')}
              <span className="data-empty-link gradient" onClick={() => navigate('/market/digital')}>
                {t('avatar.market')}
              </span>
              {t('avatar.browse')}
            </p>
          </div>
        )}

        {data && (
          <>
            <div className="module-title">
              <div className="text">{t('avatar.global_avatars')}</div>
            </div>
            <div className="page-container-cover">
              {globalData?.map((d) => (
                <div className="list-image-box" key={`${d.id}`}>
                  <Render>
                    {function LazyImage() {
                      const [ref, inView] = useInView({ triggerOnce: true })

                      return (
                        <div className="box-main">
                          <div className="image" ref={ref}>
                            {inView && (
                              <img
                                src={d.face_url || d.cover_url}
                                onClick={() => {
                                  previeGroup({
                                    ...d,
                                    global: true
                                  })
                                }}
                              />
                            )}
                          </div>
                          <div className="bottom">
                            <div className="title">{d.title}</div>
                            <Button type="primary" className="btn" onClick={() => groupToCreateVideo(d)}>
                              {t('avatar.to_create')}
                            </Button>
                            <span className="num">
                              <label className="gradient">
                                {t('avatar.material_count', { count: (d.member_count || 0) + (d.image_count || 0) })}
                              </label>
                            </span>
                          </div>
                        </div>
                      )
                    }}
                  </Render>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {group?.id && (
        <div className="common-wrapper detail-wrapper">
          <div className="container-title">{t('avatar.video_material')}</div>
          <div className="page-container">
            {!group.global && (
              <div className="list-box">
                <div className="empty-group" onClick={toChooseVideoModel}>
                  <div className="center">
                    <img className="icon-add" src={IconAdd} />
                    <div className="text gradient">{t('avatar.add_video')}</div>
                  </div>
                </div>
              </div>
            )}

            {showGroupHumans.map((d) => (
              <div className={`list-box ${openId === d.id ? 'hovered' : ''} `} key={d.id}>
                <div className="box-main" onClick={() => d.status === 2 && d.source_type !== 3 && previewVideo(d)}>
                  {d.status === 2 && d.source_type !== 3 && (
                    <div className="play">
                      <Play />
                    </div>
                  )}

                  {d.status !== 1 && (
                    <div className="op" onClick={(e) => e.stopPropagation()}>
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: '1',
                              label: (
                                <div className="dropdown-list" onClick={renameAvatar.bind(this, d)}>
                                  <EditOutlined />
                                  {t('system.rename')}
                                </div>
                              )
                            },
                            {
                              key: '2',
                              label: (
                                <div className="dropdown-list" onClick={moveGroup.bind(this, d.id)}>
                                  <DeliveredProcedureOutlined />
                                  {t('system.move_to')}
                                </div>
                              )
                            },
                            {
                              key: '3',
                              label: (
                                <div className="dropdown-list" onClick={deleteVideo.bind(this, d.id)}>
                                  <DeleteOutlined />
                                  {t('system.delete')}
                                </div>
                              )
                            },
                            {
                              key: '4',
                              label: (
                                <CopyToClipboard
                                  text={d?.id || ''}
                                  onCopy={() => {
                                    eventTracking('CopyVideoId', {
                                      id: d?.id || ''
                                    })
                                    message.success(t('system.copy_success'))
                                  }}
                                >
                                  <div className="dropdown-list">
                                    <CopyOutlined />
                                    {t('system.copy_material_id')}
                                  </div>
                                </CopyToClipboard>
                              )
                            }
                          ].filter((l) => (group?.global ? l.key === '4' : true))
                        }}
                        placement="bottomRight"
                        open={openId === d.id}
                        onOpenChange={(open: boolean) => setOpenId(open ? d.id : undefined)}
                      >
                        <div className="btn" onClick={(e) => e.stopPropagation()}>
                          <Ellipsis />
                        </div>
                      </Dropdown>
                    </div>
                  )}

                  {d.status === 1 && (
                    <div className="mask">
                      <div className="pending">
                        <LoadingOutlined />
                        <div>{t('avatar.under_review')}</div>
                      </div>
                    </div>
                  )}

                  {(d.status === 3 || d.tip) && (
                    <div className={`status ${d.status === 3 ? 'error' : ''}`}>
                      {d.status === 3 && <span>{t('system.failed')}</span>}

                      {d.tip && (
                        <Tooltip placement="bottom" title={d.tip}>
                          <span className="tips">{<InfoCircleOutlined />}</span>
                        </Tooltip>
                      )}
                    </div>
                  )}

                  <div className="photo">
                    <Render>
                      {function LoadImg() {
                        const [imgClass, setImgClass] = useState('')
                        return (
                          <img
                            className={`${imgClass}`}
                            src={urlSource(d.video_url_v2 || d.video_url, 'video')}
                            onLoad={(e: any) => {
                              const w = e.target?.naturalWidth
                              const h = e.target?.naturalHeight
                              if (w / h > 1.6) {
                                setImgClass('portrait')
                              }
                            }}
                          />
                        )
                      }}
                    </Render>

                    <img className="shadow" src={urlSource(d.video_url_v2 || d.video_url, 'video')} />
                  </div>

                  {d.special_cost && (
                    <Popover
                      content={
                        <div className="common-popover w400" onClick={(e) => e.stopPropagation()}>
                          <div>
                            <strong>{t('avatar.creation_discount')}</strong>
                          </div>
                          <ul>
                            <li>
                              {t('avatar.discount_description_1', { cost: d.creation_cost_per_second })}
                              <div className="ml10">
                                a. {t('avatar.audio_driven_cost', { cost: d.creation_cost_per_second })}
                              </div>
                              <div className="ml10">
                                b. {t('avatar.text_driven_cost', { cost: d.creation_cost_per_second + 1 })}
                              </div>
                            </li>
                            <li>{t('avatar.discount_description_2')}</li>
                          </ul>
                        </div>
                      }
                      placement="bottom"
                    >
                      <div className="discount" onClick={(e) => e.stopPropagation()}>
                        <span>{t('avatar.creation_discount')}</span>
                        <ExclamationCircleOutlined />
                      </div>
                    </Popover>
                  )}
                </div>

                <div className="bottom">
                  <div className="box-title">{d.title}</div>
                  {d.status === 2 && (
                    <Button type="primary" className="btn" onClick={toCreateVideo.bind(this, d)}>
                      {t('avatar.to_create')}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {!!showGroupHumans?.length && showGroupHumans?.length !== groupHumans?.length && (
            <div className="view-more mt8">
              <span className="gradient" onClick={loadVideoMore}>
                {t('system.show_more')}
              </span>
            </div>
          )}

          {!group.global && showGroupPhotos && (
            <>
              <div className="container-title">{t('avatar.photo_material')}</div>
              <div className="page-container">
                <div className="list-box">
                  <div className="empty-group" onClick={toChoosePhotoModel}>
                    <div className="center">
                      <img className="icon-add" src={IconAdd} />
                      <div className="text gradient">{t('avatar.add_photo')}</div>
                    </div>
                  </div>
                </div>

                {showGroupPhotos?.map((d) => (
                  <div className={`list-box ${openId === d.id ? 'hovered' : ''} `} key={d.id}>
                    <div className="box-main">
                      {d.status !== 1 && !group?.global && (
                        <div className="op" onClick={(e) => e.stopPropagation()}>
                          <Dropdown
                            menu={{
                              items: [
                                {
                                  key: '1',
                                  label: (
                                    <div
                                      className="dropdown-list"
                                      onClick={renameAvatar.bind(this, {
                                        ...d,
                                        type: 'photo'
                                      })}
                                    >
                                      <EditOutlined />
                                      {t('system.rename')}
                                    </div>
                                  )
                                },
                                {
                                  key: '3',
                                  label: (
                                    <div className="dropdown-list" onClick={deletePhoto.bind(this, d.id)}>
                                      <DeleteOutlined />
                                      {t('system.delete')}
                                    </div>
                                  )
                                }
                              ]
                            }}
                            placement="bottomRight"
                            open={openId === d.id}
                            onOpenChange={(open: boolean) => setOpenId(open ? d.id : undefined)}
                          >
                            <div className="btn" onClick={(e) => e.stopPropagation()}>
                              <Ellipsis />
                            </div>
                          </Dropdown>
                        </div>
                      )}

                      <div className="photo">
                        <img src={d.url} />

                        <img className="shadow" src={d.url} />
                      </div>
                    </div>

                    <div className="bottom">
                      <div className="box-title">{d.title}</div>
                      <Button
                        type="primary"
                        className="btn"
                        onClick={() => {
                          setPhotoInfo(d)
                          setCreateAiMaterialOpen(true)
                          setCreateAiMaterialMode(2)
                        }}
                      >
                        {t('avatar.generate_video_material')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {!!showGroupPhotos?.length && showGroupPhotos?.length !== groupPhotos?.length && (
            <div className="view-more mt8">
              <span className="gradient" onClick={loadPhotoMore}>
                {t('system.show_more')}
              </span>
            </div>
          )}

          {!!groupCreations?.length && (
            <>
              <div className="container-title">{t('avatar.works')}</div>
              <div className="page-container">
                {groupCreations?.map((d) => (
                  <VideoBox key={d.id} d={d} update={getGroupCreations} />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <Modal
        open={!!modelModal}
        title={t('avatar.model_selection')}
        footer={null}
        onCancel={() => setModelModal(undefined)}
      >
        <div className="avatar-model">
          {modelModal === 'avatar' && (
            <>
              <div className="model-item" onClick={toCreatePhotoAvatar}>
                <div className="left">
                  <img src={AvatarPhoto} />
                </div>
                <div className="center">
                  <div className="title">{t('avatar.photo_generated_avatar')}</div>
                  <div className="desc">{t('avatar.photo_generated_desc')}</div>
                </div>
              </div>
              <div className="model-item" onClick={toCreateAvatar}>
                <div className="left">
                  <img src={AvatarVideo} />
                </div>
                <div className="center w300">
                  <div className="title">{t('avatar.video_generated_avatar')}</div>
                  <div className="desc">{t('avatar.video_generated_desc')}</div>
                </div>
                <div className="right recommond">
                  <span className="gradient">{t('avatar.recommended_choice')}</span>
                </div>
              </div>
              <div className="model-item" onClick={toCreateAIAvatar}>
                <div className="left">
                  <img src={AvatarAi} />
                </div>
                <div className="center">
                  <div className="title">{t('avatar.ai_generated_avatar')}</div>
                  <div className="desc">{t('avatar.ai_generated_desc')}</div>
                </div>
              </div>
            </>
          )}

          {modelModal === 'video' && (
            <>
              <div className="model-item" onClick={toCreateAvatar}>
                <div className="left">
                  <img src={AvatarPhoto} />
                </div>
                <div className="center">
                  <div className="title">{t('avatar.local_upload_video')}</div>
                  <div className="desc">{t('avatar.local_upload_video_desc')}</div>
                </div>
              </div>
              <div
                className="model-item"
                onClick={() => {
                  setPhotoInfo(undefined)
                  setCreateAiMaterialOpen(true)
                  setCreateAiMaterialMode(2)
                  setModelModal(undefined)
                }}
              >
                <div className="left">
                  <img src={AvatarVideo} />
                </div>
                <div className="center">
                  <div className="title">{t('avatar.ai_generated_video')}</div>
                  <div className="desc">{t('avatar.ai_generated_video_desc')}</div>
                </div>
              </div>
            </>
          )}

          {modelModal === 'photo' && (
            <>
              <div className="model-item" onClick={toCreatePhotoAvatar}>
                <div className="left">
                  <img src={AvatarPhoto} />
                </div>
                <div className="center">
                  <div className="title">{t('avatar.local_upload_photo')}</div>
                  <div className="desc">{t('avatar.local_upload_photo_desc')}</div>
                </div>
              </div>
              <div
                className="model-item"
                onClick={() => {
                  setPhotoInfo(undefined)
                  setCreateAiMaterialOpen(true)
                  setCreateAiMaterialMode(1)
                  setModelModal(undefined)
                }}
              >
                <div className="left">
                  <img src={AvatarAi} />
                </div>
                <div className="center">
                  <div className="title">{t('avatar.ai_generated_photo')}</div>
                  <div className="desc">{t('avatar.ai_generated_photo_desc')}</div>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>

      <VideoModal
        preview={preview}
        onCancel={() => setPreview(undefined)}
        btnShow={true}
        btnClick={toCreateVideo.bind(this, preview)}
      />

      <CreateAvatarDrawer
        open={createDrawerOpen}
        onCancel={() => setCreateDrawerOpen(false)}
        onOk={onCreateSuccess}
        group={group}
      />

      <CreatePhotoAvatarDrawer
        open={createPhotoDrawerOpen}
        onCancel={() => setCreatePhotoDrawerOpen(false)}
        onOk={onCreateSuccess.bind(this)}
        group={group}
      />

      <CreateAiAvatarNewDrawer
        open={createAIDrawerOpen}
        onCancel={() => setCreateAIModalOpen(false)}
        onOk={onCreateSuccess.bind(this, 2)}
      />

      <Modal
        open={!!editAvatar?.id}
        title={t('system.rename')}
        okText={t('system.confirm')}
        cancelText={t('system.cancel')}
        onCancel={() => setEditAvatar(undefined)}
        onOk={sureRename}
        okButtonProps={{
          disabled: !editAvatar?.title?.trim()
        }}
      >
        <div style={{ padding: 20 }}>
          <Input
            style={{ height: 40 }}
            maxLength={20}
            value={editAvatar?.title}
            onChange={(e) =>
              setEditAvatar({
                ...editAvatar,
                title: e.target.value
              })
            }
          />
        </div>
      </Modal>

      <CreateAiMaterialDrawer
        open={createAiMaterialOpen}
        onCancel={() => {
          setCreateAiMaterialOpen(false)
          onCreateSuccess()
        }}
        group={group}
        mode={createAiMaterialMode}
        photo={photoInfo}
      />

      <AvatarFormModalEdit
        open={formModalOpen}
        onCancel={() => setFormModalOpen(false)}
        data={group}
        onSuccess={(form) =>
          setGroup({
            ...group,
            ...form
          })
        }
      />

      <Groups avatarId={avatarId} onSuccess={getGroupHumans} onCancel={() => setAvatarId(undefined)} />

      <Modal
        onCancel={() => setTipType(undefined)}
        width={600}
        open={!!tipType}
        title={null}
        className="commom-modal create-success-tip-modal"
        footer={null}
      >
        <div className="content">
          <img className="circle-1" src={MarketCircle1} />
          <img className="circle-2" src={MarketCircle2} />
          <img className="circle-3" src={MarketCircle3} />
          <div className="img">
            <img src={group?.face_url || group?.cover_url} />
          </div>
        </div>
        <div className="title">{t('avatar.create_success_title')}</div>
        <div className="desc">{t('avatar.create_success_desc')}</div>

        <div className="footer">
          <Button onClick={() => setTipType(undefined)}>{t('system.do_later')}</Button>
          <Button
            type="primary"
            onClick={() => {
              setTipType(undefined)
              setPhotoInfo(groupPhotos?.[0])
              setCreateAiMaterialOpen(true)
              setCreateAiMaterialMode(tipType)
            }}
          >
            {t('avatar.ai_generate_material')}
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default memo(Index)
